import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { qrLookup, placePallet } from '@/lib/api';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Camera, Flashlight, FlashlightOff, Search, MapPin, Package, Box, X, Keyboard, Check, Move, Truck, Printer, Eye, AlertTriangle, RefreshCw, Link2 } from 'lucide-react';
import jsQR from 'jsqr';
import { CATEGORY_COLORS } from '@/lib/constants';

const SCAN_INTERVAL_MS = 67;

export default function ScanPage() {
  const [scanning, setScanning] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [result, setResult] = useState(null);
  const [cameraFailed, setCameraFailed] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [placementMode, setPlacementMode] = useState(null);
  const [placingLoading, setPlacingLoading] = useState(false);
  const [readyPallets, setReadyPallets] = useState([]);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkSearch, setLinkSearch] = useState('');
  const [linkingPalletId, setLinkingPalletId] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const placeId = searchParams.get('place');
    const placeRef = searchParams.get('ref');
    if (placeId && placeRef) {
      setPlacementMode({ palletId: placeId, palletRef: placeRef });
    }
  }, [searchParams]);

  useEffect(() => {
    api.get('/pallets?status=ready').then(r => {
      const d = r.data;
      const list = Array.isArray(d) ? d : (Array.isArray(d?.pallets) ? d.pallets : []);
      setReadyPallets(list);
    }).catch((err) => { console.error('Failed to load ready pallets:', err); });
  }, []);

  // FIX 2: properly tear down any existing stream before stopping
  const stopCamera = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setScanning(false);
    setTorchOn(false);
  }, []);

  const handleQRResult = useCallback(async (data) => {
    try {
      const res = await qrLookup(data);
      const scanData = res.data;
      if (placementMode && scanData.type === 'location') {
        setResult({ ...scanData, _placementConfirm: true });
        return;
      }
      setResult(scanData);
    } catch {
      toast.error('QR lookup failed');
    }
  }, [placementMode]);

  const scanFrame = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !streamRef.current) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (video.readyState === video.HAVE_ENOUGH_DATA && video.videoWidth > 0) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height);
      if (code) {
        try {
          const data = JSON.parse(code.data);
          if (navigator.vibrate) navigator.vibrate(50);
          handleQRResult(data);
          stopCamera();
        } catch (e) { /* non-JSON QR code — skip silently */ }
      }
    }
  }, [stopCamera, handleQRResult]);

  // FIX 2: Always tear down existing stream first, then create fresh one
  const startCamera = useCallback(async () => {
    // Tear down any existing stream before starting new one
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setCameraFailed(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      streamRef.current = stream;
      // iOS Safari requires playsInline — do not remove
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        video.setAttribute('autoplay', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('muted', '');
        video.muted = true;
        await new Promise((resolve) => {
          video.onloadedmetadata = () => resolve();
          setTimeout(resolve, 1000);
        });
        try { await video.play(); } catch (e) {
          setTimeout(async () => { try { await video.play(); } catch (e2) { /* retry exhausted */ } }, 200);
        }
      }
      setScanning(true);
      setPermissionGranted(true);
      const track = stream.getVideoTracks()[0];
      if (track.getCapabilities) {
        const capabilities = track.getCapabilities();
        setTorchSupported(!!capabilities.torch);
      }
      intervalRef.current = setInterval(scanFrame, SCAN_INTERVAL_MS);
    } catch (err) {
      console.error('Camera error:', err);
      setCameraFailed(true);
      toast.error('Camera access denied. Use manual entry below.');
    }
  }, [scanFrame]);

  const toggleTorch = async () => {
    if (!streamRef.current) return;
    const track = streamRef.current.getVideoTracks()[0];
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn(!torchOn);
    } catch { toast.error('Torch toggle failed'); }
  };

  useEffect(() => {
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'camera' }).then(p => {
        if (p.state === 'granted') {
          setPermissionGranted(true);
          startCamera();
        }
      }).catch(() => { /* permission API not supported on this device */ });
    }
    return () => stopCamera();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleManualSearch = async () => {
    if (!manualInput.trim()) return;
    const input = manualInput.trim().toUpperCase();
    if (/^[A-D]\d{2}-L\d-P\d$/.test(input)) {
      try {
        const res = await qrLookup({ t: 'loc', s: input });
        setResult(placementMode ? { ...res.data, _placementConfirm: true } : res.data);
      } catch { toast.error('Location not found'); }
    } else if (input.startsWith('SFX-') && !input.includes('-B')) {
      try { const res = await qrLookup({ t: 'pal', ref: input }); setResult(res.data); } catch { toast.error('Pallet not found'); }
    } else if (/^SFX-\d+-B\d+$/.test(input)) {
      try { const res = await qrLookup({ t: 'box', ref: input }); setResult(res.data); } catch { toast.error('Box not found'); }
    } else {
      try { const res = await qrLookup({ t: 'pal', ref: input }); setResult(res.data); } catch { navigate(`/pallet/${input}`); }
    }
  };

  const handleConfirmPlacement = async (slotAddress) => {
    if (!placementMode) return;
    setPlacingLoading(true);
    try {
      await placePallet(placementMode.palletId, { slot_address: slotAddress });
      toast.success(`${placementMode.palletRef} placed at ${slotAddress}`);
      navigate(`/pallet/${placementMode.palletRef}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Placement failed');
    } finally {
      setPlacingLoading(false);
    }
  };

  // Place an arbitrary ready pallet at the currently scanned (empty) slot
  const handleLinkPalletToSlot = async (pal) => {
    const slot = result?.location?.slot_address;
    if (!slot) return;
    setLinkingPalletId(pal.id);
    try {
      await placePallet(pal.id, { slot_address: slot });
      toast.success(`${pal.pallet_ref} linked to ${slot}`);
      setLinkDialogOpen(false);
      setLinkSearch('');
      navigate(`/pallet/${pal.pallet_ref}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Link failed');
    } finally {
      setLinkingPalletId(null);
    }
  };

  const dismissResult = () => {
    setResult(null);
  };

  const scanAgain = () => {
    setResult(null);
    startCamera();
  };

  // ==========================================
  // RENDER: Placement confirmation is shown FIRST before everything
  // ==========================================
  const showPlacementConfirm = result && result._placementConfirm && result.type === 'location' && placementMode;
  const showLocationResult = result && result.type === 'location' && !showPlacementConfirm;
  const showPalletResult = result && result.type === 'pallet';
  const showBoxResult = result && result.type === 'box';
  const hasResult = showPlacementConfirm || showLocationResult || showPalletResult || showBoxResult;

  return (
    <div className="space-y-3 max-w-lg mx-auto pb-8" data-testid="scan-page">
      <h1 className="text-xl font-bold text-gray-900">
        {placementMode ? `Place ${placementMode.palletRef}` : 'Scan QR Code'}
      </h1>

      {placementMode && !hasResult && (
        <Card className="border-0 shadow-sm border-l-4 border-l-[#1a2fce]">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 text-sm text-[#1a2fce]">
              <Package size={14} />
              <span className="font-semibold">Placement Mode:</span>
              <span className="font-mono">{placementMode.palletRef}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Scan a slot QR code to place this pallet</p>
          </CardContent>
        </Card>
      )}

      {/* ===== FIX 1: PLACEMENT CONFIRMATION — shown FIRST, above everything ===== */}
      {showPlacementConfirm && (
        <Card className="border-0 shadow-lg border-2 border-[#1a2fce]" data-testid="placement-confirmation">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin size={24} className="text-[#1a2fce]" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Place pallet at location</p>
                <p className="text-xl font-mono font-bold text-[#0d1f6e]">{placementMode.palletRef}</p>
                <p className="text-lg font-mono font-bold text-[#1a2fce]">{result.location?.slot_address}</p>
              </div>
            </div>
            {result.pallets?.length > 0 && (
              <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                This slot already has {result.pallets.length} pallet(s). Placement will add another.
              </div>
            )}
            <Button className="w-full h-12 text-sm font-bold" style={{ backgroundColor: '#1a2fce' }}
              onClick={() => handleConfirmPlacement(result.location?.slot_address)}
              disabled={placingLoading}
              data-testid="placement-confirm">
              <Check size={16} className="mr-2" />
              {placingLoading ? 'Placing...' : `Place ${placementMode.palletRef} Here`}
            </Button>
            <Button variant="outline" className="w-full h-10" onClick={scanAgain} data-testid="placement-scan-different">
              <RefreshCw size={14} className="mr-2" /> Scan Different Slot
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ===== FIX 4: LOCATION SCAN RESULT (non-placement) — contextual by status ===== */}
      {showLocationResult && (
        <Card className="border-0 shadow-sm" data-testid="scan-result-location">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapPin size={18} className="text-[#1a2fce]" />
                <span className="font-mono font-bold text-lg">{result.location?.slot_address}</span>
              </div>
              <Badge variant="outline" className="text-[10px]">
                {result.pallets?.length > 0 ? `${result.pallets.length} pallet(s)` : 'Empty'}
              </Badge>
            </div>

            {result.pallets?.length > 0 ? (
              <div className="space-y-2">
                {result.pallets.map((p) => {
                  const cat = p.items?.[0]?.category || 'other';
                  const catColor = CATEGORY_COLORS[cat] || '#6b7280';
                  const isQcHold = p.status === 'qc_hold';
                  return (
                    <div key={p.pallet_ref || p.id} className="border rounded-lg overflow-hidden" style={{ borderLeftWidth: 3, borderLeftColor: catColor }}>
                      <div className="p-3 cursor-pointer hover:bg-gray-50" onClick={() => navigate(`/pallet/${p.pallet_ref}`)} data-testid={`slot-pallet-${p.pallet_ref}`}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-sm">{p.pallet_ref}</span>
                          <Badge variant={isQcHold ? 'destructive' : 'outline'} className="text-[9px]">{p.status}</Badge>
                        </div>
                        <div className="text-xs text-gray-600 mt-1">{p.items?.[0]?.description}</div>
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          {p.items?.reduce((s, it) => s + (it.qty_pcs_total || 0), 0)?.toLocaleString()} pcs ·
                          {p.items?.reduce((s, it) => s + (it.qty_weight_net_kg || 0), 0)?.toFixed(1)} kg
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-2" data-testid="empty-slot-link-area">
                <div className="text-sm text-gray-500 py-1">
                  Empty slot — link a ready pallet here
                </div>
                {readyPallets.length === 0 ? (
                  <div className="text-xs text-gray-400 italic border border-dashed rounded-lg p-3 text-center">
                    No pallets are in <span className="font-semibold">ready</span> status. Pack a pallet first, or use Admin → Data → Force-Edit to link an existing pallet.
                  </div>
                ) : (
                  <>
                    <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1" data-testid="ready-pallet-list">
                      {readyPallets.slice(0, 6).map((pal) => {
                        const cat = pal.items?.[0]?.category || 'other';
                        const catColor = CATEGORY_COLORS[cat] || '#6b7280';
                        const desc = pal.items?.[0]?.description || 'No items';
                        const totalPcs = pal.items?.reduce((s, i) => s + (i.qty_pcs_total || 0), 0) || 0;
                        return (
                          <div
                            key={pal.id}
                            className="flex items-center gap-2 border rounded-lg p-2"
                            style={{ borderLeftWidth: 3, borderLeftColor: catColor }}
                            data-testid={`ready-pallet-row-${pal.pallet_ref}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="font-mono font-bold text-xs">{pal.pallet_ref}</div>
                              <div className="text-[10px] text-gray-500 truncate">{desc}</div>
                              <div className="text-[10px] text-gray-400">{totalPcs.toLocaleString()} pcs</div>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => handleLinkPalletToSlot(pal)}
                              disabled={linkingPalletId === pal.id}
                              className="h-8 text-[11px] gap-1"
                              style={{ backgroundColor: '#0e7a45' }}
                              data-testid={`place-ready-${pal.pallet_ref}`}
                            >
                              <Check size={12} /> Place Here
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                    {readyPallets.length > 6 && (
                      <div className="text-[10px] text-gray-400 text-center">
                        Showing 6 of {readyPallets.length}. Use “Link Pallet” below to search all.
                      </div>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => setLinkDialogOpen(true)}
                      className="w-full h-9 text-xs gap-1 border-[#1a2fce] text-[#1a2fce]"
                      data-testid="open-link-pallet-dialog"
                    >
                      <Link2 size={12} /> Link Pallet to This Slot ({readyPallets.length} ready)
                    </Button>
                  </>
                )}
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1 h-9 text-xs gap-1" onClick={() => navigate(`/map?slot=${result.location?.slot_address}`)} data-testid="action-view-slot">
                <Eye size={12} /> View on Map
              </Button>
              <Button variant="outline" className="flex-1 h-9 text-xs gap-1" onClick={scanAgain} data-testid="action-scan-again">
                <RefreshCw size={12} /> Scan Again
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ===== FIX 3: PALLET SCAN RESULT — with Issue/Dispatch and QC Hold ===== */}
      {showPalletResult && (
        <Card className="border-0 shadow-sm" data-testid="scan-result-pallet">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Package size={18} className="text-[#1a2fce]" />
              <span className="font-mono font-bold text-lg">{result.pallet?.pallet_ref}</span>
              <Badge variant={result.pallet?.status === 'qc_hold' ? 'destructive' : 'outline'} className="text-xs">{result.pallet?.status}</Badge>
            </div>
            <div className="text-sm text-gray-600">
              {result.pallet?.items?.[0]?.description}
              {result.pallet?.slot_address && <span className="ml-2 text-xs text-gray-400">@ {result.pallet.slot_address}</span>}
            </div>
            <div className="text-xs text-gray-400">
              {result.pallet?.items?.reduce((s, i) => s + (i.qty_pcs_total || 0), 0)?.toLocaleString()} pcs · {result.pallet?.items?.reduce((s, i) => s + (i.qty_weight_net_kg || 0), 0)?.toFixed(1)} kg · {result.pallet?.boxes?.length || 0} boxes
            </div>

            {result.pallet?.status === 'qc_hold' && (
              <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700" data-testid="qc-hold-badge">
                <AlertTriangle size={14} /> QC Hold — Cannot Issue
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 pt-1">
              <Button variant="outline" className="h-9 text-xs gap-1" onClick={() => navigate(`/pallet/${result.pallet?.pallet_ref}`)} data-testid="action-view-pallet">
                <Eye size={12} /> View Details
              </Button>
              {/* FIX 3: Issue/Dispatch for placed AND partial pallets */}
              {(result.pallet?.status === 'placed' || result.pallet?.status === 'partial') && (
                <Button variant="outline" className="h-9 text-xs gap-1" onClick={() => navigate(`/issue/${result.pallet?.id}`)} data-testid="action-issue-pallet">
                  <Truck size={12} /> Issue / Dispatch
                </Button>
              )}
              {result.pallet?.status === 'ready' && (
                <Button className="h-9 text-xs gap-1" style={{ backgroundColor: '#0e7a45' }}
                  onClick={() => { setPlacementMode({ palletId: result.pallet?.id, palletRef: result.pallet?.pallet_ref }); setResult(null); startCamera(); }}
                  data-testid="action-scan-to-place">
                  <MapPin size={12} /> Scan to Place
                </Button>
              )}
              {(result.pallet?.status === 'placed' || result.pallet?.status === 'partial') && (
                <Button variant="outline" className="h-9 text-xs gap-1" onClick={() => { setPlacementMode({ palletId: result.pallet?.id, palletRef: result.pallet?.pallet_ref }); setResult(null); startCamera(); }} data-testid="action-move-pallet">
                  <Move size={12} /> Move Pallet
                </Button>
              )}
              <Button variant="outline" className="h-9 text-xs gap-1" onClick={scanAgain} data-testid="action-scan-again-pallet">
                <RefreshCw size={12} /> Scan Again
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ===== FIX 7: BOX SCAN RESULT — full context and actions ===== */}
      {showBoxResult && (() => {
        const box = result.box || {};
        const pallet = result.pallet || {};
        const item = result.item || {};
        const isIssued = box.status === 'issued';
        const isQcHold = pallet.status === 'qc_hold';
        const isVoided = pallet.status === 'voided';
        const catColor = CATEGORY_COLORS[item.category] || '#6b7280';
        const totalBoxes = box.total_boxes_on_pallet || '?';
        const boxNum = box.box_number || '?';

        return (
          <Card className="border-0 shadow-sm overflow-hidden" data-testid="scan-result-box" style={{ borderLeftWidth: 4, borderLeftColor: catColor }}>
            <CardContent className="p-4 space-y-3">
              {/* Box header */}
              <div>
                <div className="flex items-center gap-2">
                  <Box size={18} className="text-[#0891b2]" />
                  <span className="font-mono font-bold text-lg">{box.box_ref}</span>
                  <Badge variant={isIssued ? 'secondary' : box.is_exception ? 'outline' : 'outline'} className={`text-[10px] ${isIssued ? 'bg-green-100 text-green-700' : ''}`}>
                    {isIssued ? 'Issued' : box.is_exception ? 'Exception' : 'Packed'}
                  </Badge>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">Box {boxNum} of {totalBoxes}</p>
              </div>

              {/* Item info */}
              <div className="space-y-1">
                <div className="text-sm font-semibold text-gray-800">{item.description}</div>
                <div className="font-mono text-xs text-gray-500">{item.item_code}</div>
                {(item.size || item.grade || item.standard || item.finish) && (
                  <div className="text-xs text-gray-500">
                    {[item.size, item.grade, item.standard, item.finish].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>

              {/* Quantities */}
              <div className="grid grid-cols-3 gap-2 p-2 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <div className="text-base font-bold text-gray-900">{(box.qty_pcs || 0).toLocaleString()}</div>
                  <div className="text-[10px] text-gray-500">Qty (pcs)</div>
                </div>
                <div className="text-center">
                  <div className="text-base font-bold text-gray-900">{box.weight_net_kg || 0}</div>
                  <div className="text-[10px] text-gray-500">Net (kg)</div>
                </div>
                <div className="text-center">
                  <div className="text-base font-bold text-gray-900">{box.weight_gross_kg || 0}</div>
                  <div className="text-[10px] text-gray-500">Gross (kg)</div>
                </div>
              </div>

              {/* Batch/Heat */}
              {(box.batch_no || box.heat_no) && (
                <div className="text-xs text-gray-500">
                  {box.batch_no && <span>Batch: <strong>{box.batch_no}</strong></span>}
                  {box.batch_no && box.heat_no && <span> · </span>}
                  {box.heat_no && <span>Heat: <strong>{box.heat_no}</strong></span>}
                </div>
              )}

              {/* Parent pallet summary */}
              <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <div className="text-[10px] text-blue-600 font-medium mb-1">Part of pallet:</div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-sm text-blue-900 cursor-pointer hover:underline" onClick={() => navigate(`/pallet/${pallet.pallet_ref}`)} data-testid="box-parent-link">{pallet.pallet_ref}</span>
                  <Badge variant={isQcHold ? 'destructive' : isVoided ? 'secondary' : 'outline'} className="text-[9px]">{pallet.status}</Badge>
                  {pallet.slot_address ? (
                    <span className="text-[10px] text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">{pallet.slot_address}</span>
                  ) : (
                    <span className="text-[10px] text-gray-400">Location Pending</span>
                  )}
                </div>
              </div>

              {/* Status-specific banners */}
              {isIssued && (
                <div className="p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700" data-testid="box-issued-info">
                  Issued {box.issued_date ? `on ${new Date(box.issued_date).toLocaleDateString()}` : ''} {box.challan_ref ? `· Challan ${box.challan_ref}` : '· Issued internally'}
                </div>
              )}
              {isQcHold && !isIssued && (
                <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 flex items-center gap-2" data-testid="box-qc-hold-banner">
                  <AlertTriangle size={14} /> Parent pallet {pallet.pallet_ref} is on QC Hold — this box cannot be issued
                </div>
              )}
              {isVoided && (
                <div className="p-2 bg-gray-100 border border-gray-200 rounded text-xs text-gray-600" data-testid="box-voided-banner">
                  This pallet has been voided {pallet.void_reason ? `— ${pallet.void_reason}` : ''} {pallet.date_voided ? `— ${new Date(pallet.date_voided).toLocaleDateString()}` : ''}
                </div>
              )}

              {/* Action buttons based on status */}
              {!isVoided && (
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <Button variant="outline" className="h-9 text-xs gap-1" onClick={() => navigate(`/pallet/${pallet.pallet_ref}`)} data-testid="action-view-parent-pallet">
                    <Package size={12} /> View Parent Pallet
                  </Button>
                  {pallet.slot_address && (
                    <Button variant="outline" className="h-9 text-xs gap-1" onClick={() => navigate(`/map?slot=${pallet.slot_address}`)} data-testid="action-view-slot-map">
                      <MapPin size={12} /> View Slot on Map
                    </Button>
                  )}
                  {!isIssued && !isQcHold && (pallet.status === 'placed' || pallet.status === 'partial') && (
                    <Button variant="outline" className="h-9 text-xs gap-1 col-span-2" onClick={() => navigate(`/issue/${pallet.id}`)} data-testid="action-issue-this-box">
                      <Truck size={12} /> Issue This Box
                    </Button>
                  )}
                  <Button variant="outline" className="h-9 text-xs gap-1 col-span-2" onClick={scanAgain} data-testid="action-scan-again-box">
                    <RefreshCw size={12} /> Scan Again
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Camera section — only show when no result displayed */}
      {!hasResult && (
        <Card className="border-0 shadow-sm overflow-hidden">
          <CardContent className="p-0 relative">
            {scanning ? (
              <div className="relative bg-black">
                {/* iOS Safari requires playsInline — do not remove */}
                <video ref={videoRef} autoPlay playsInline muted webkit-playsinline="true"
                  className="w-full h-72 object-cover" style={{ objectFit: 'cover', WebkitTransform: 'scaleX(1)' }} data-testid="camera-video" />
                <canvas ref={canvasRef} className="hidden" />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-52 h-52 border-2 border-white/60 rounded-xl shadow-lg" />
                </div>
                <div className="absolute top-2 right-2 flex gap-2">
                  {torchSupported && (
                    <Button onClick={toggleTorch} className="h-9 w-9 p-0 rounded-full bg-black/50 hover:bg-black/70" data-testid="torch-toggle">
                      {torchOn ? <FlashlightOff size={16} className="text-yellow-300" /> : <Flashlight size={16} className="text-white" />}
                    </Button>
                  )}
                  <Button onClick={stopCamera} className="h-9 w-9 p-0 rounded-full bg-black/50 hover:bg-black/70" data-testid="stop-scan">
                    <X size={16} className="text-white" />
                  </Button>
                </div>
                <div className="absolute bottom-2 left-0 right-0 text-center">
                  <span className="text-xs text-white/80 bg-black/40 px-3 py-1 rounded-full">
                    {placementMode ? 'Point at SLOT QR code' : 'Point at QR code'}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <Camera size={48} className="text-gray-300 mb-4" />
                {cameraFailed && <p className="text-sm text-red-500 mb-3" data-testid="camera-failed">Camera unavailable. Use manual entry below.</p>}
                <Button onClick={startCamera} className="h-12 px-8 text-sm font-semibold" style={{ backgroundColor: '#1a2fce' }} data-testid="start-scan-btn">
                  <Camera size={16} className="mr-2" /> {permissionGranted ? 'Resume Camera' : 'Open Camera'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Manual Entry — always visible */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
            <Keyboard size={14} /> Manual Entry
          </div>
          <div className="flex gap-2">
            <Input value={manualInput} onChange={e => setManualInput(e.target.value)}
              placeholder={placementMode ? 'Enter slot address (e.g. B03-L2-P1)' : 'Pallet ref, slot address, or box ref'}
              className="h-11 text-sm" onKeyDown={e => e.key === 'Enter' && handleManualSearch()} data-testid="manual-search-input" />
            <Button onClick={handleManualSearch} className="h-11 px-4" style={{ backgroundColor: '#1a2fce' }} data-testid="manual-search-btn">
              <Search size={16} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Link Pallet to Slot — full search dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={(o) => { setLinkDialogOpen(o); if (!o) setLinkSearch(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Link2 size={16} className="text-[#1a2fce]" />
              Link Pallet to {result?.location?.slot_address}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              autoFocus
              value={linkSearch}
              onChange={(e) => setLinkSearch(e.target.value.toUpperCase())}
              placeholder="Search by pallet ref or item..."
              className="h-10 text-sm"
              data-testid="link-pallet-search"
            />
            <div className="max-h-80 overflow-y-auto space-y-1.5 -mx-1 px-1">
              {(() => {
                const q = linkSearch.trim().toLowerCase();
                const filtered = q
                  ? readyPallets.filter((p) => {
                      if (p.pallet_ref?.toLowerCase().includes(q)) return true;
                      return (p.items || []).some((i) =>
                        (i.description || '').toLowerCase().includes(q) ||
                        (i.item_code || '').toLowerCase().includes(q)
                      );
                    })
                  : readyPallets;
                if (filtered.length === 0) {
                  return (
                    <div className="text-xs text-gray-400 italic text-center py-6">
                      No matching ready pallets
                    </div>
                  );
                }
                return filtered.map((pal) => {
                  const cat = pal.items?.[0]?.category || 'other';
                  const catColor = CATEGORY_COLORS[cat] || '#6b7280';
                  const desc = pal.items?.[0]?.description || 'No items';
                  const totalPcs = pal.items?.reduce((s, i) => s + (i.qty_pcs_total || 0), 0) || 0;
                  return (
                    <div
                      key={pal.id}
                      className="flex items-center gap-2 border rounded-lg p-2 hover:bg-gray-50"
                      style={{ borderLeftWidth: 3, borderLeftColor: catColor }}
                      data-testid={`link-pallet-row-${pal.pallet_ref}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-mono font-bold text-xs">{pal.pallet_ref}</div>
                        <div className="text-[11px] text-gray-600 truncate">{desc}</div>
                        <div className="text-[10px] text-gray-400">{totalPcs.toLocaleString()} pcs</div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleLinkPalletToSlot(pal)}
                        disabled={linkingPalletId === pal.id}
                        className="h-8 text-[11px] gap-1"
                        style={{ backgroundColor: '#0e7a45' }}
                        data-testid={`link-confirm-${pal.pallet_ref}`}
                      >
                        <Check size={12} /> Place Here
                      </Button>
                    </div>
                  );
                });
              })()}
            </div>
            <div className="text-[10px] text-gray-400">
              Only pallets in <span className="font-semibold">ready</span> status are listed. To override and force-link a pallet in another status, use Admin → Data → Force-Edit.
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
